import React, { useContext, useState } from "react";
import { makeStyles } from '@material-ui/core/styles';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Typography,
    Paper,
    Chip,
    Hidden
} from "@material-ui/core";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { CartContext } from "../../contexts/CartContext";

const MembershipComparisonBlock = (props) => {
    const cartContext = useContext(CartContext);
    const { block } = props;
    const [addingToCart, setAddingToCart] = useState({});
    const [selectedMembership, setSelectedMembership] = useState(null);

    // Check which membership is currently in cart on component mount
    React.useEffect(() => {
        const membershipProductIds = block.memberships
            .filter(m => m.product?.id)
            .map(m => m.product.id.toString());
        
        // Find which membership product is in the cart
        const cartProductIds = Object.keys(cartContext.products);
        const selectedProductId = cartProductIds.find(id => 
            membershipProductIds.includes(id)
        );
        
        if (selectedProductId) {
            const membership = block.memberships.find(m => 
                m.product?.id?.toString() === selectedProductId
            );
            setSelectedMembership(membership);
        }
    }, [block.memberships, cartContext.products]);

    const useStyles = makeStyles((theme) => ({
        root: {
            marginBottom: '50px',
            width: '100%'
        },
        title: {
            textAlign: 'center',
            marginBottom: theme.spacing(2),
            fontWeight: 'bold'
        },
        subtitle: {
            textAlign: 'center',
            marginBottom: theme.spacing(4),
            color: theme.palette.text.secondary
        },
        tableContainer: {
            marginBottom: theme.spacing(3),
            overflow: 'auto'
        },
        headerCell: {
            fontWeight: 'bold',
            backgroundColor: theme.palette.grey[50],
            textAlign: 'center',
            minWidth: 180,
            padding: theme.spacing(2)
        },
        featureCell: {
            fontWeight: 'bold',
            backgroundColor: theme.palette.grey[50],
            padding: theme.spacing(2),
            minWidth: 200
        },
        membershipHeader: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: theme.spacing(2)
        },
        membershipName: {
            fontWeight: 'bold',
            fontSize: '1.2rem',
            marginBottom: theme.spacing(1)
        },
        membershipSubtitle: {
            fontSize: '0.9rem',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1),
            textAlign: 'center'
        },
        membershipPrice: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            marginBottom: theme.spacing(1)
        },
        priceDescription: {
            fontSize: '0.8rem',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(2)
        },
        bestValueChip: {
            backgroundColor: '#FFD700',
            color: '#000',
            fontWeight: 'bold',
            marginBottom: theme.spacing(2)
        },
        buyButton: {
            minWidth: 120,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            }
        },
        selectedButton: {
            minWidth: 120,
            backgroundColor: theme.palette.success.main,
            color: 'white',
            '&:hover': {
                backgroundColor: theme.palette.success.dark,
            }
        },
        selectedColumn: {
            backgroundColor: theme.palette.action.selected,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRight: `3px solid ${theme.palette.primary.main}`
        },
        valueCell: {
            textAlign: 'center',
            padding: theme.spacing(1.5),
            verticalAlign: 'middle'
        },
        checkIcon: {
            color: theme.palette.success.main
        },
        closeIcon: {
            color: theme.palette.error.main
        },
        mobileCard: {
            marginBottom: theme.spacing(3),
            padding: theme.spacing(3),
            position: 'relative'
        },
        selectedMobileCard: {
            marginBottom: theme.spacing(3),
            padding: theme.spacing(3),
            position: 'relative',
            backgroundColor: theme.palette.action.selected,
            border: `2px solid ${theme.palette.primary.main}`
        },
        mobileHeader: {
            textAlign: 'center',
            marginBottom: theme.spacing(3)
        },
        featuresList: {
            marginBottom: theme.spacing(3)
        },
        featureItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(1, 0),
            borderBottom: `1px solid ${theme.palette.grey[200]}`
        },
        featureName: {
            fontWeight: 500
        },
        mobileButtonContainer: {
            textAlign: 'center'
        }
    }));

    const classes = useStyles();

    const handleAddToCart = async (membership) => {
        if (!membership.product) return;

        // If this membership is already selected, do nothing
        if (selectedMembership?.product?.id === membership.product.id) return;

        setAddingToCart(prev => ({ ...prev, [membership.product.id]: true }));

        try {
            // Remove previously selected membership from cart
            if (selectedMembership?.product?.id) {
                cartContext.removeProduct(selectedMembership.product.id.toString());
            }

            // Add new membership to cart
            cartContext.updateProduct(
                membership.product.id.toString(),
                membership.product.displayName || membership.name,
                1,
                membership.product.price,
                membership.product.quantityAvailable,
                membership.product.amountSold,
                membership.product.productActivityInProgressText
            );

            // Update selected membership state
            setSelectedMembership(membership);
        } finally {
            setTimeout(() => {
                setAddingToCart(prev => ({ ...prev, [membership.product.id]: false }));
            }, 1000);
        }
    };

    const renderFeatureValue = (membership, feature) => {
        const featureValues = membership.featureValues || {};
        const value = featureValues[feature.featureName];

        if (value === true || value === "true" || value === "✓" || value === "yes") {
            return <CheckIcon className={classes.checkIcon} />;
        } else if (value === false || value === "false" || value === "✗" || value === "no") {
            return <CloseIcon className={classes.closeIcon} />;
        } else if (value) {
            return <Typography variant="body2">{value}</Typography>;
        } else {
            return <CloseIcon className={classes.closeIcon} />;
        }
    };

    const DesktopView = () => (
        <TableContainer id="membership-comparison-table-container" component={Paper} className={classes.tableContainer}>
            <Table id="membership-comparison-table">
                <TableHead id="membership-comparison-table-head">
                    <TableRow id="membership-comparison-header-row">
                        <TableCell id="membership-comparison-features-header" className={classes.featureCell}>Features</TableCell>
                        {block.memberships.map((membership, index) => {
                            const isSelected = selectedMembership?.product?.id === membership.product?.id;
                            return (
                            <TableCell 
                                key={index}
                                id={`membership-header-${index}`}
                                className={`${classes.headerCell} ${isSelected ? classes.selectedColumn : ''}`}
                            >
                                <div id={`membership-header-content-${index}`} className={classes.membershipHeader}>
                                    {membership.isBestValue && (
                                        <Chip
                                            id={`membership-best-value-chip-${index}`}
                                            label="BEST VALUE"
                                            className={classes.bestValueChip}
                                            size="small"
                                        />
                                    )}
                                    <Typography id={`membership-name-${index}`} className={classes.membershipName}>
                                        {membership.name}
                                    </Typography>
                                    {membership.subtitle && (
                                        <Typography id={`membership-subtitle-${index}`} className={classes.membershipSubtitle}>
                                            {membership.subtitle}
                                        </Typography>
                                    )}
                                    <Typography id={`membership-price-${index}`} className={classes.membershipPrice}>
                                        ${membership.price}
                                    </Typography>
                                    {membership.priceDescription && (
                                        <Typography id={`membership-price-description-${index}`} className={classes.priceDescription}>
                                            {membership.priceDescription}
                                        </Typography>
                                    )}
                                    <Button
                                        id={`membership-buy-button-${index}`}
                                        variant="contained"
                                        className={isSelected ? classes.selectedButton : classes.buyButton}
                                        onClick={() => handleAddToCart(membership)}
                                        disabled={addingToCart[membership.product?.id] || !membership.product || isSelected}
                                    >
                                        {isSelected ? 'Selected' : (addingToCart[membership.product?.id] ? 'Adding...' : (membership.buttonText || 'Buy Now'))}
                                    </Button>
                                </div>
                            </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody id="membership-comparison-table-body">
                    {block.features?.map((feature, featureIndex) => (
                        <TableRow key={featureIndex} id={`feature-row-${featureIndex}`}>
                            <TableCell id={`feature-name-cell-${featureIndex}`} className={classes.featureCell}>
                                <Typography id={`feature-name-${featureIndex}`} variant="body2" className={classes.featureName}>
                                    {feature.featureName}
                                </Typography>
                                {feature.featureDescription && (
                                    <Typography id={`feature-description-${featureIndex}`} variant="caption" color="textSecondary">
                                        {feature.featureDescription}
                                    </Typography>
                                )}
                            </TableCell>
                            {block.memberships.map((membership, membershipIndex) => {
                                const isSelected = selectedMembership?.product?.id === membership.product?.id;
                                return (
                                <TableCell 
                                    key={membershipIndex}
                                    id={`feature-value-${featureIndex}-${membershipIndex}`}
                                    className={`${classes.valueCell} ${isSelected ? classes.selectedColumn : ''}`}
                                >
                                    {renderFeatureValue(membership, feature)}
                                </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const MobileView = () => (
        <Grid id="membership-comparison-mobile-container" container spacing={3}>
            {block.memberships.map((membership, index) => {
                const isSelected = selectedMembership?.product?.id === membership.product?.id;
                return (
                <Grid item xs={12} key={index} id={`mobile-membership-${index}`}>
                    <Paper id={`mobile-membership-card-${index}`} className={isSelected ? classes.selectedMobileCard : classes.mobileCard}>
                        {membership.isBestValue && (
                            <Chip
                                id={`mobile-best-value-chip-${index}`}
                                label="BEST VALUE"
                                className={classes.bestValueChip}
                                size="small"
                                style={{ position: 'absolute', top: 16, right: 16 }}
                            />
                        )}
                        <div id={`mobile-membership-header-${index}`} className={classes.mobileHeader}>
                            <Typography id={`mobile-membership-name-${index}`} className={classes.membershipName}>
                                {membership.name}
                            </Typography>
                            {membership.subtitle && (
                                <Typography id={`mobile-membership-subtitle-${index}`} className={classes.membershipSubtitle}>
                                    {membership.subtitle}
                                </Typography>
                            )}
                            <Typography id={`mobile-membership-price-${index}`} className={classes.membershipPrice}>
                                ${membership.price}
                            </Typography>
                            {membership.priceDescription && (
                                <Typography id={`mobile-membership-price-description-${index}`} className={classes.priceDescription}>
                                    {membership.priceDescription}
                                </Typography>
                            )}
                        </div>
                        
                        <div id={`mobile-features-list-${index}`} className={classes.featuresList}>
                            {block.features?.map((feature, featureIndex) => (
                                <div key={featureIndex} id={`mobile-feature-item-${index}-${featureIndex}`} className={classes.featureItem}>
                                    <div id={`mobile-feature-info-${index}-${featureIndex}`}>
                                        <Typography id={`mobile-feature-name-${index}-${featureIndex}`} variant="body2" className={classes.featureName}>
                                            {feature.featureName}
                                        </Typography>
                                        {feature.featureDescription && (
                                            <Typography id={`mobile-feature-description-${index}-${featureIndex}`} variant="caption" color="textSecondary">
                                                {feature.featureDescription}
                                            </Typography>
                                        )}
                                    </div>
                                    <div id={`mobile-feature-value-${index}-${featureIndex}`}>
                                        {renderFeatureValue(membership, feature)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div id={`mobile-button-container-${index}`} className={classes.mobileButtonContainer}>
                            <Button
                                id={`mobile-buy-button-${index}`}
                                variant="contained"
                                className={isSelected ? classes.selectedButton : classes.buyButton}
                                onClick={() => handleAddToCart(membership)}
                                disabled={addingToCart[membership.product?.id] || !membership.product || isSelected}
                                fullWidth
                            >
                                {isSelected ? 'Selected' : (addingToCart[membership.product?.id] ? 'Adding...' : (membership.buttonText || 'Buy Now'))}
                            </Button>
                        </div>
                    </Paper>
                </Grid>
                );
            })}
        </Grid>
    );

    if (!block.memberships || block.memberships.length === 0) {
        return null;
    }

    return (
        <div id="membership-comparison-block" className={classes.root}>
            {block.title && (
                <Typography id="membership-comparison-title" variant="h4" className={classes.title}>
                    {block.title}
                </Typography>
            )}
            {block.subtitle && (
                <Typography id="membership-comparison-subtitle" variant="h6" className={classes.subtitle}>
                    {block.subtitle}
                </Typography>
            )}
            
            <Hidden smDown id="membership-comparison-desktop-view">
                <DesktopView />
            </Hidden>
            
            <Hidden mdUp id="membership-comparison-mobile-view">
                <MobileView />
            </Hidden>
        </div>
    );
};

export default MembershipComparisonBlock;